import { Form } from 'antd';
import type { FormInstance } from 'antd/es/form';
import type { NamePath, Store } from 'antd/es/form/interface';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

const { Item } = Form;

// https://github.com/react-component/field-form/blob/master/src/utils/typeUtil.ts
function toArray<T>(value?: T | T[] | null): T[] {
  if (value === undefined || value === null) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
}

// https://github.com/react-component/util/blob/master/src/utils/get.ts
function get(
  entity: Record<PropertyKey, unknown>,
  path: (string | number)[] | readonly (string | number)[]
) {
  let current: unknown = entity;

  for (let i = 0; i < path.length; i += 1) {
    if (current === null || current === undefined) {
      return undefined;
    }

    current = (current as Record<PropertyKey, unknown>)[path[i]];
  }

  return current;
}

const valOf = (values: Store, name: NamePath) => {
  const namePath = toArray(name);

  return get(values as Record<PropertyKey, unknown>, namePath);
};

export type WatchProps<T = unknown> = {
  name?: NamePath;
  list?: NamePath[];
  children?: (next: T, prev: T, form: FormInstance) => ReactNode;
  onlyValid?: boolean;
  onChange?: (next: T, prev: T, form: FormInstance) => void;
};

export function WatchingCell<T = unknown>(
  props: WatchProps<T>
): JSX.Element | null {
  const { name, list, children, onlyValid = false, onChange } = props;

  const hasName = !Array.isArray(list);
  const prev = useRef<unknown>(
    hasName ? undefined : list?.map(() => undefined)
  );

  return (
    <Item
      noStyle
      shouldUpdate={(prevStore: Store, nextStore: Store) => {
        if (hasName) {
          return name
            ? valOf(prevStore, name) !== valOf(nextStore, name)
            : true;
        }

        return (list as NamePath[]).some(
          (key) => key && valOf(prevStore, key) !== valOf(nextStore, key)
        );
      }}
    >
      {(rawForm) => {
        const form = rawForm as FormInstance & Record<string, unknown>;

        const next = (
          hasName
            ? name && form.getFieldValue(name)
            : (list as NamePath[]).map((key) => key && form.getFieldValue(key))
        ) as T;

        let hasChange;
        let hasValidValue = false;
        let changeEffect = null;

        if (hasName) {
          hasChange = next !== (prev.current as T);
          hasValidValue = next !== undefined;
        } else {
          hasChange = (next as unknown[]).some(
            (val: unknown, index: number) => {
              hasValidValue = val !== undefined;

              return val !== (prev.current as unknown[])?.[index];
            }
          );
        }

        const cachedPrev = prev.current as T;

        if (hasChange) {
          prev.current = next;

          if (onChange) {
            const ChangeEffect = () => {
              useEffect(() => onChange(next, cachedPrev, form), []);

              return null;
            };

            changeEffect = <ChangeEffect />;
          }
        }

        if (!onlyValid || (onlyValid && hasValidValue)) {
          return (
            <>
              {children?.(next, cachedPrev, form)}
              {changeEffect}
            </>
          );
        }

        return changeEffect;
      }}
    </Item>
  );
}
